import { dist2, iatan2, idiv, imax, imin, isqrt, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy } from '../types.ts';
import type { PenBoar, PensRules, PensState, Pt } from '../caza-types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The broken corral (docs/sendas/barbaro.md, chapter 1, act 1, «El corral roto»; the ground in battle/caza-layout.ts):
 * the clan's winter pens by night, in the rain. Twelve stone boars huddle against the far wall. The pack leaps the wall
 * in bands; of each band one (or two) goes for a boar, takes it in its jaws and drags it to the nearest stone of the wall
 * (`drag`, the 3 s of its mark): felled before it gets there, the boar trots back to the herd; at the wall it leaps out
 * with it, gone. The rest go for the hero. Halfway (`grayAt` felled), the Grey Wolf comes over the wall, takes a boar and
 * goes: while it carries one it can't be stopped, only seen. Won with `need` of the pack felled and `keep` boars in the
 * corral; lost with fewer, or the hero down.
 *
 * Cost: a dozen boars and the pack's wolves (a handful at once), looked at once a tick; nothing walks every nightmare
 * against every other.
 */

const fresh = (r: PensRules): PensState => ({
  boars: r.homes.map((h) => ({ x: h.x, y: h.y, facing: 768, by: 0, lost: false, moving: false })),
  pack: [],
  next: r.first,
  crow: r.crows.first,
  kills: 0,
  taken: 0,
  saved: 0,
  gray: { phase: 0, x: r.grayIn.x, y: r.grayIn.y, facing: 0, boar: -1, t: 0 },
  done: -1,
});

/** The boars still in the corral (not over the wall, not in the Grey Wolf's jaws). */
export const pensKept = (w: PensState): number => {
  let n = 0;
  for (const b of w.boars) if (!b.lost) n++;
  return n;
};

/** The wall's stone nearest a point (where a wolf leaps out with what it carries). */
const exitFor = (r: PensRules, p: Pt): Pt => {
  let best = r.wall[0] as Pt;
  let bd = 0x7fffffff;
  for (const w of r.wall) {
    const d = dist2(w.x, w.y, p.x, p.y);
    if (d < bd) {
      bd = d;
      best = w;
    }
  }
  return best;
};

/** The free boar nearest a point (not taken, not gone, not already some wolf's), or -1. */
const freeBoar = (w: PensState, x: number, y: number): number => {
  let best = -1;
  let bd = 0x7fffffff;
  for (let i = 0; i < w.boars.length; i++) {
    const b = w.boars[i] as PenBoar;
    if (b.lost || b.by || w.pack.some((p) => p.boar === i)) continue;
    const d = dist2(b.x, b.y, x, y);
    if (d < bd) {
      bd = d;
      best = i;
    }
  }
  return best;
};

/**
 * A band over the wall: `band` wolves in two bunches at two stones of the rim far apart (a third of the ring at least),
 * the first bunch's leader for a boar (`grab`), the second's too `twoBp` of the time: the hero can't be at both.
 */
const band = (s: BattleState, r: PensRules, w: PensState): void => {
  const n = rngRange(s.rng, r.band[0], r.band[1]);
  const R = r.rim.length;
  const i0 = rngInt(s.rng, R);
  const i1 = (i0 + idiv(R, 3) + rngInt(s.rng, idiv(R, 3) + 1)) % R;
  const grabs = [r.grab > 0, rngInt(s.rng, 10000) < r.twoBp];
  for (let k = 0; k < n; k++) {
    const g = k % 2;
    const at = r.rim[g ? i1 : i0] as Pt;
    const j = idiv(k, 2);
    const e = spawnEnemy(s, 'wolf', 1, false, 0, at.x + (j % 2 ? 380 : -380) * imax(1, j), at.y + j * 320, r.wolfHp);
    const boar = j === 0 && grabs[g] ? freeBoar(w, e.x, e.y) : -1;
    w.pack.push({ id: e.id, boar, exit: { x: 0, y: 0 }, until: 0, speed: 0 });
    if (j === 0) ev(s, { t: 'senda', kind: 'band', npc: '', x: at.x, y: at.y, n });
  }
};

/** The Grey Wolf: over the wall, to the herd, a boar in its jaws, out over the far wall. Nobody stops it. */
const grayTick = (s: BattleState, r: PensRules, w: PensState, cue: (c: string) => void): void => {
  const g = w.gray;
  if (g.phase === 0) {
    if (w.kills < r.grayAt) return;
    g.phase = 1;
    g.t = s.tick;
    ev(s, { t: 'senda', kind: 'grayIn', npc: '', x: g.x, y: g.y });
    cue('gray');
  }
  if (g.phase === 1) {
    // To the free boar nearest it (one a wolf drags will do, if none is free).
    if (g.boar < 0 || (w.boars[g.boar] as PenBoar).lost) {
      g.boar = freeBoar(w, g.x, g.y);
      if (g.boar < 0) g.boar = w.boars.findIndex((b) => !b.lost);
      if (g.boar < 0) {
        g.phase = 4;
        return;
      }
    }
    const b = w.boars[g.boar] as PenBoar;
    if (stepToward(g, b.x, b.y, r.gray)) {
      // A wolf dragging it lets go (it runs from the Grey Wolf).
      for (const p of w.pack) if (p.boar === g.boar) p.boar = -1;
      b.by = -1;
      b.lost = true;
      g.phase = 2;
      g.t = s.tick;
      ev(s, { t: 'senda', kind: 'grayGrab', npc: '', x: b.x, y: b.y, n: g.boar });
    }
    return;
  }
  if (g.phase === 2) {
    if (s.tick < g.t + 8) return;
    g.phase = 3;
    return;
  }
  if (g.phase === 3) {
    const b = w.boars[g.boar] as PenBoar;
    const done = stepToward(g, r.grayOut.x, r.grayOut.y, r.gray);
    b.x = g.x;
    b.y = g.y;
    if (done) {
      g.phase = 4;
      ev(s, { t: 'senda', kind: 'grayOut', npc: '', x: g.x, y: g.y, n: g.boar });
    }
  }
};

export const pensTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.pens;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.pens ??= fresh(r));
  if (!s.started || w.done >= 0) return;
  // The pack: who fell (a felled dragger lets its boar go), who leaped out with its boar.
  let k = 0;
  for (const p of w.pack) {
    const e = s.enemies.find((x) => x.id === p.id);
    if (!e || e.hp <= 0) {
      w.kills++;
      if (p.boar >= 0) {
        const b = w.boars[p.boar] as PenBoar;
        if (b.by === p.id) {
          b.by = 0;
          w.saved++;
          ev(s, { t: 'senda', kind: 'saved', npc: '', x: b.x, y: b.y, n: p.boar });
          cue(`saved${w.saved}`);
        }
      }
      continue;
    }
    if (p.until > 0 && s.tick >= p.until && withinRange(e.x, e.y, p.exit.x, p.exit.y, 160)) {
      const b = w.boars[p.boar] as PenBoar;
      b.lost = true;
      b.by = -1;
      w.taken++;
      e.hp = 0;
      // (Over the wall, not felled: it counts for nothing.)
      s.enemies = s.enemies.filter((x) => x.id !== e.id);
      ev(s, { t: 'senda', kind: 'taken', npc: '', x: e.x, y: e.y, n: p.boar });
      cue(`taken${w.taken}`);
      continue;
    }
    w.pack[k++] = p;
  }
  w.pack.length = k;
  // The boars set free trot home; the rest stand in the huddle.
  for (let i = 0; i < w.boars.length; i++) {
    const b = w.boars[i] as PenBoar;
    b.moving = false;
    if (b.lost || b.by) continue;
    const h = r.homes[i] as Pt;
    if (b.x !== h.x || b.y !== h.y) {
      b.moving = true;
      stepToward(b, h.x, h.y, r.home);
      if (b.x === h.x && b.y === h.y) b.facing = 768;
    }
  }
  if (w.grabs) cue('grab1');
  grayTick(s, r, w, cue);
  if (w.kills >= r.need) {
    w.done = s.tick;
    // The rest of the pack goes off after the Wolf.
    s.enemies = s.enemies.filter((e) => !w.pack.some((p) => p.id === e.id) && e.kind !== 'crow');
    w.pack.length = 0;
    return;
  }
  if (s.waveT >= w.next) {
    w.next = s.waveT + rngRange(s.rng, r.every[0], r.every[1]);
    band(s, r, w);
  }
  if (s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    const at = r.rim[rngInt(s.rng, r.rim.length)] as Pt;
    for (let c = 0; c < r.crows.n; c++) spawnEnemy(s, 'crow', 1, false, 0, at.x + c * 400, at.y - c * 300, imax(1, idiv(r.wolfHp, 3)));
  }
};

/** A wolf of the pack after its boar, or dragging it to the wall (true: the corral moves it). */
export const pensHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.pens;
  const w = s.senda?.pens;
  if (!r || !w || e.kind !== 'wolf' || e.stun > 0) return false;
  const p = w.pack.find((x) => x.id === e.id);
  if (!p || p.boar < 0) return false;
  const b = w.boars[p.boar] as PenBoar;
  if (b.lost || (b.by && b.by !== e.id)) {
    // Someone else has it: the next free one, or the hero.
    p.boar = freeBoar(w, e.x, e.y);
    p.until = 0;
    if (p.boar < 0) return false;
    return true;
  }
  if (!p.until) {
    e.anim = 'walk';
    if (!withinRange(e.x, e.y, b.x, b.y, r.reach)) {
      stepToward(e, b.x, b.y, px(e));
      return true;
    }
    // In its jaws: to the nearest stone of the wall, there in `drag`.
    b.by = e.id;
    p.exit = exitFor(r, b);
    const d = isqrt(dist2(e.x, e.y, p.exit.x, p.exit.y));
    p.speed = imax(1, idiv(d + r.drag - 1, r.drag));
    p.until = s.tick + r.drag;
    w.grabs = (w.grabs ?? 0) + 1;
    ev(s, { t: 'senda', kind: 'grab', npc: '', x: b.x, y: b.y, n: p.boar });
    return true;
  }
  // Dragging: the boar in its jaws, backwards to the wall.
  e.anim = 'walk';
  if (e.x !== p.exit.x || e.y !== p.exit.y) e.facing = iatan2(p.exit.y - e.y, p.exit.x - e.x);
  stepToward(e, p.exit.x, p.exit.y, p.speed);
  b.x = e.x;
  b.y = e.y;
  b.facing = e.facing;
  return true;
};

/** Twelve of the pack, felled. */
export const pensGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.pens;
  const w = s.senda?.pens;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: imin(w.kills, r.need), need: r.need };
};

/** Won with twelve felled and the herd kept; lost with fewer than `keep` boars left. */
export const pensResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.pens;
  const w = s.senda?.pens;
  if (!r || !w) return null;
  if (pensKept(w) < r.keep) return 'lost';
  if (w.done >= 0 && s.tick >= w.done + 18) return 'won';
  return null;
};
